import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as CodeMirror from 'codemirror';
import { Editor, EditorChangeLinkedList, InputStyle } from 'codemirror';
import { Subject } from 'rxjs';

import { CodemirrorContext } from '../codemirror-context';
import { ClickedMarksAndMouseEvent, CodeMirrorMark, MarksHelper } from './marks-helper';

/**
 * Heavily inspired by https://github.com/scttcper/ngx-codemirror/blob/master/src/lib/codemirror.component.ts
 */
@Component({
  selector: 'app-codemirror-wrapper',
  templateUrl: './codemirror-wrapper.component.html',
  styleUrls: ['./codemirror-wrapper.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeMirrorWrapperComponent),
      multi: true,
    },
  ],
})
export class CodeMirrorWrapperComponent implements OnDestroy, AfterViewInit, ControlValueAccessor {
  @Input() ctx: CodemirrorContext;

  /* called when the editor is focused or loses focus */
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() focus = new EventEmitter<void>();

  @ViewChild('ref') ref: ElementRef;

  value = ''; // only used to remember the value set by angular before this.codeMirror is built

  marksClicked$ = new Subject<ClickedMarksAndMouseEvent>();

  // references to event handlers, so that we can unregister them on destroy
  private blurEventHandler: () => any;
  private focusEventHandler: () => any;
  private clickEventHandler: (cm: Editor, event: MouseEvent) => void;
  private changeEventHandler: (cm: Editor, change: EditorChangeLinkedList) => void;
  private cursorActivityEventHandler: () => void;

  // callbacks set by angular by calling ControlValueAccessor
  private onChangeFn: (value: string) => void;
  private onTouchedFn: () => void;

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    const options = {
      lineWrapping: true,
      cursorHeight: 0.85, // loooks more similar to a native browser textarea with 0.85

      // U+200D (zero-width joiner) is part of many complex emojis, let's avoid CM wrapping it inside a <span class="cm-invalidchar">
      // eslint-disable-next-line no-control-regex
      specialChars: /[\u0000-\u001f\u007f-\u009f\u00ad\u061c\u200b\u200c\u200e\u200f\u2028\u2029\ufeff\ufff9-\ufffc]/,

      // without this the hidden textarea hack for copy/paste in browser context menu works erratically on FF
      resetSelectionOnContextMenu: false,

      // some 3rd pary tools are only compatible with the 'contenteditable' mode
      inputStyle: 'contenteditable' as InputStyle,

      extraKeys: {
        Esc: () => this.ngZone.run(() => this.ctx.escapePressed$.next()),
        Tab: () => this.ngZone.run(() => this.ctx.tabPressed$.next()),
      },
    };

    this.blurEventHandler = () => this.ngZone.run(() => this.focusChanged(false));
    this.focusEventHandler = () => this.ngZone.run(() => this.focusChanged(true));
    this.clickEventHandler = (cm: Editor, event: Event) => this.ngZone.run(() => this.onMouseDown(cm, event));
    this.changeEventHandler = (cm: Editor, change: EditorChangeLinkedList) => {
      this.ngZone.run(() => this.codemirrorValueChanged(cm, change));
    };
    this.cursorActivityEventHandler = () => this.ngZone.run(() => this.ctx.cursorActivity$.next());

    this.ngZone.runOutsideAngular(() => {
      this.ctx.codeMirror = CodeMirror.fromTextArea(this.ref.nativeElement, options);
      this.ctx.codeMirror.on('blur', this.blurEventHandler);
      this.ctx.codeMirror.on('focus', this.focusEventHandler);
      this.ctx.codeMirror.on('mousedown', this.clickEventHandler);
      this.ctx.codeMirror.on('change', this.changeEventHandler);
      this.ctx.codeMirror.on('cursorActivity', this.cursorActivityEventHandler);
      this.ctx.codeMirror.setValue(this.value);
    });
  }

  ngOnDestroy(): void {
    this.ctx.codeMirror.off('blur', this.blurEventHandler);
    this.ctx.codeMirror.off('focus', this.focusEventHandler);
    this.ctx.codeMirror.off('mousedown', this.clickEventHandler);
    this.ctx.codeMirror.off('change', this.changeEventHandler);
    this.ctx.codeMirror.off('cursorActivity', this.cursorActivityEventHandler);
    this.ctx.codeMirror.toTextArea();
  }

  doFocus(): void {
    this.ctx.codeMirror.focus();

    // This fixes a couple of glitch in "SF widget" mode:
    // - the cursor wasn't there when first expanding the editor
    //   (because it was init'ed earlier, when CodeMirror wasn't visible yet)
    // - the cursor would behave weirdly when pasting an emoji from the modal
    //   (I couldn't figure out why, and why not in desktop mode)
    // This is a convenient enough fix that I leave this called even when not necessary
    // (not necessary when switching from editor editor in a multi-message tweet, for example)
    // The impact on performance should be absolutely negligible.
    this.ctx.codeMirror.refresh();
  }

  insertTextAtCursorPosition(text: string): void {
    const cursor = this.ctx.codeMirror.getDoc().getCursor();
    this.ctx.codeMirror.getDoc().replaceRange(text, cursor);
  }

  private codemirrorValueChanged(cm: Editor, change: EditorChangeLinkedList): void {
    if (change.origin !== 'setValue') {
      this.value = cm.getValue();
      this.onChangeFn(this.value);
    }
  }

  private focusChanged(focused: boolean): void {
    if (focused) {
      this.focus.emit();
    } else {
      // let angular form know that this control has been touched
      this.onTouchedFn();
    }
  }

  private onMouseDown(editor: Editor, event) {
    if (event.button === 0) {
      const coordinate = { left: event.pageX, top: event.pageY };
      const pos = editor.coordsChar(coordinate);
      const marksAtClick = editor.getDoc().findMarksAt(pos) as CodeMirrorMark[];
      if (marksAtClick.length > 0 && MarksHelper.targetIsMark(event)) {
        this.marksClicked$.next({
          marks: marksAtClick,
          event,
        });
      }
    }
  }

  /* Implements ControlValueAccessor. */
  writeValue(value: string): void {
    this.value = value || '';
    if (this.ctx.codeMirror) {
      this.ctx.codeMirror.setValue(this.value);
    }
  }

  /* Implements ControlValueAccessor. */
  registerOnChange(fn: (value: string) => void): void {
    this.onChangeFn = fn;
  }
  /* Implements ControlValueAccessor. */
  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }
  /* Implements ControlValueAccessor. */
  setDisabledState(isDisabled: boolean): void {
    this.ctx.codeMirror.setOption('readOnly', isDisabled ? 'nocursor' : false);
  }
}
